/* eslint max-lines: "off" */

import { isCommercialBrand } from 'helpers/brandContext'
import { commercial, consumerRTHChart, paywallFeatureCards, residential, rthChart } from './palettes'
import type { ComponentColorMap } from './types'

// COMPONENT COLORS:

export const loadComponentColors = (): ComponentColorMap => {
  return {
    swiperTheme: residential.darkBlue,
    swiperBackground: residential.blackTransparent,
    executableFocusOutline: residential.blueDark,
    nonExecutableFocusOutline: residential.greyDark,

    alertFormPreviewBackground: residential.greyLightest,
    ancillaryInfoHeaderBackground: residential.greyLighter,
    background: residential.greyLighter,
    noteBackground: residential.greyLightest,
    markBackground: residential.yellowDarkest,
    alternatingRowBackground: residential.greyLightest,
    backgroundHover: residential.greyLighter,
    border: residential.greyLight,
    customerFilterPrefixColumn: residential.greyLight,
    devicePanelHeader: residential.greyDark,
    parentDivider: residential.greyLight,
    divider: residential.greyLighter,
    draftAlert: residential.greyDarker,
    draftAlertHover: residential.greyDark,
    disabled: residential.greyLighter,
    remoteDevicesBorder: residential.grayDim,

    danger: residential.red,
    dangerText: residential.white,

    featureTourInfoBackground: residential.blueLightest,
    featureTourWarningBackground: residential.yellowLightest,

    featureToursActiveBackground: residential.white,
    featureToursPublishedBackground: residential.greyLight,
    featureToursDisabledBackground: residential.grey,
    featureToursSiteLink: residential.blueDarkest,
    featureToursHeading: residential.blue,

    mainContentBorder: residential.greyLighter,
    migrateDealerSpinner: residential.blueDark,
    modalOverlay: residential.translucent,

    navbarBackground: isCommercialBrand() ? commercial.white : residential.white,
    navbarBackgroundHover: isCommercialBrand() ? commercial.grayDarker : residential.greyLighter,
    navbarBackgroundHoverBorder: isCommercialBrand() ? commercial.grayDarker : residential.blueDark,
    navbarBackgroundActive: isCommercialBrand() ? commercial.grayDarker : residential.blue,
    navbarArrowBackground: isCommercialBrand() ? commercial.grayDarker : residential.blue,
    navbarArrowBackgroundHover: isCommercialBrand() ? commercial.grayDarker : residential.blueDark,
    navbarHoverText: residential.blueDark,

    pageSpinner: residential.blue,
    paginationBackground: residential.greyLighter,
    paneBackground: residential.white,
    positiveStatus: residential.green,
    premiumFreeBoxText: residential.greyDark,
    premiumFreeTrialText: residential.redDarker,
    serverErrorBannerBackground: residential.redSoft,
    impersonationBannerBackground: residential.yellowLight,
    rthChartAxis: residential.grey,
    rthChartGridLine: residential.greyLight,
    rthChartIconHoverBackground: residential.greyLightest,
    rthLegendControlBackground: residential.greyLight,
    rthLegendControlBackgroundHover: residential.greyLighter,
    rthLegendBorderHover: residential.greyDark,
    siteMessageInfoBackground: residential.blueLightest,
    siteMessageWarningBackground: residential.yellowLightest,
    tableHeaderBackground: residential.greyLighter,
    paginationItemActiveBackground: residential.greyLighter,
    paginationItemHoverBackground: residential.greyLighter,
    newLabelBackground: isCommercialBrand() ? commercial.grayDark : residential.blue,
    disabledAlertNotification: residential.greyLight,
    deviceMetricsListTitleBackground: residential.greyLight,
    tagBackground: residential.greyLighter,
    tagBorder: residential.greyLight,
    rowBackground: residential.greyBlueLight,
    rowHoverBackground: residential.bluePaler,
    chargeItemBackground: residential.translucentVeryLight,
    inputFocusRing: residential.blueVeryDark,
    notesCardBackground: residential.graySoft,
    titleBackground: residential.greyBlue,
    scrollbarThumb: residential.greyStrong,
    scrollbarThumbBorder: residential.whiteTranslucent,
    flagBackground: residential.dimSoftRed,
    hoverBackground: residential.darkerGray,

    // borders

    ancillaryInfoPanelBorder: residential.greyLighter,
    tooltipBorder: residential.greyLight,
    tooltipShadow: residential.blackShadow,
    siteMessageEnabledStatusBorder: residential.greyDarker,
    StatusEnabledBorder: residential.greyDarker,
    menuItemSplit: residential.greyLighter,
    menuItemSectionSplit: residential.greyLight,
    datePickerBorder: residential.greyLighter,
    datePickerHover: residential.greyLighter,
    datePickerLableText: residential.greyDarkest,
    featureTourActiveBorder: residential.greyDarker,
    featureTourPublishedBorder: residential.greyDarker,
    featureTourAddButton: residential.blue,
    borderGray: residential.neutralGray,
    fieldsetBorder: residential.silver,
    wistiaVideoTableRowBorder: residential.greyLight,
    columnBorder: residential.greyLightest,
    borderTop: residential.lighterGray,
    rowBorder: residential.bluePale,
    iconBorder: residential.black,
    baseBorder: residential.gray,
    inputBorder: residential.darkGrayLighter,

    // text

    primaryText: residential.greyDarkest,
    secondaryText: residential.greyDark,
    checkboxText: residential.greyDarker,
    inputOptionText: residential.greyDarker,
    disabledText: residential.greyLight,
    ancillaryText: residential.grey,
    noDataText: residential.grey,
    imageCaptionText: residential.white,
    imageCaptionBackground: residential.greyDarker,
    alertSectionNumber: residential.greyDarker,
    alertSubsection: isCommercialBrand() ? commercial.blueDark : residential.blueDarkest,
    anchor: isCommercialBrand() ? commercial.blueDark : residential.blue,
    anchorHover: residential.blueDark,
    recentlyAddedText: residential.lightBlue,
    customerNameText: residential.greyDarkest,
    devicePanelHeaderText: residential.white,
    draftAlertText: residential.white,
    errorMessage: residential.redDarker,
    filteredByMessage: residential.greyDark,
    footerText: residential.greyDarker,
    flagText: residential.brightRed,

    featureToursActiveText: residential.white,
    featureToursPublishedText: residential.greyDarker,
    featureToursDisabledText: residential.grey,
    featureToursUpdatedAt: residential.greyDarker,

    headerText: residential.greyDarkest,
    helpText: residential.grayDimSoft,
    instructionText: residential.greyDarker,
    infoPanelHeaderText: residential.greyDark,
    navLinkText: residential.white,
    navLinkTextNotSelected: residential.dimGray,
    navMenuTextHover: residential.greyLight,
    watermarkTitle: residential.greyLight,
    watermarkDescription: residential.greyDark,
    rthChartAxisLabel: residential.greyDark,
    rthChartDate: residential.greyDarkest,
    rthChartInstructions: residential.greyDark,
    rthLegendControlsLabel: residential.greyDark,
    titleText: residential.greyDarkest,
    tooltipText: residential.greyDarker,
    rthLegendControl: residential.greyDarkest,
    rthLegendTime: residential.grey,
    selectedRthLegendControl: residential.black,
    rthHelpText: residential.grey,
    siteMessageStatusEnabledText: residential.white,
    siteMessageStatusDisabledText: residential.grey,
    statusDisabledText: residential.grey,
    statusEnabledText: residential.white,
    siteMessageUpdatedAt: residential.greyDarker,
    subNavText: residential.greyDarkest,
    paginationItemTextSelected: residential.greyDarkest,
    customerDirectoryIndex: residential.black,
    warrantyText: residential.black,
    filteredText: residential.yellow,
    successText: residential.green,
    successIcon: residential.green,
    errorText: residential.red,
    panelFieldLabel: residential.grey,
    newLabelText: residential.white,
    subNavbarActive: residential.blue,
    subNavbarActiveHover: residential.blue,
    tagText: residential.greyDarkest,
    deviceTooltipLabelText: residential.black,
    linkVerificationReportText: residential.blue,
    homeownerAlertText: residential.greyDarker,
    premiumText: residential.greyDarker,
    premiumHighlight: residential.orangeRed,
    customerInstructionText: residential.black,
    thirdHeaderText: residential.black,

    premiumModalTitleText: residential.blue,
    premiumModalDaysRemaining: residential.redDarker,
    premiumModalFailureText: residential.brightRed,
    hehmModalSuccessText: residential.blueCobalt,
    modalContentText: residential.greyCharcoal,
    dashboardDeviceText: residential.greyDarkest,

    // buttons

    primaryButton: isCommercialBrand() ? commercial.green : residential.blue,
    primaryButtonText: residential.white,
    primaryButtonHover: isCommercialBrand() ? commercial.greenLight : residential.blueDark,
    primaryButtonFocus: isCommercialBrand() ? commercial.greenLight : residential.blueDarker,
    primaryButtonProcessing: isCommercialBrand() ? commercial.greenLight : residential.blueDark,
    primaryButtonSpinner: residential.white,
    primaryButtonDisabled: residential.blueLight,

    secondaryButton: residential.white,
    secondaryButtonBorder: residential.blue,
    secondaryButtonText: residential.greyDarkest,
    secondaryButtonHoverBorder: residential.greyLightest,
    secondaryButtonHoverText: residential.blueDark,
    secondaryButtonHover: residential.greyLightest,
    secondaryButtonActive: residential.greyLight,
    secondaryButtonProcessing: residential.greyLightest,
    secondaryButtonSpinner: residential.greyDark,

    tertiaryButton: residential.white,
    tertiaryButtonText: residential.greyDarkest,
    tertiaryButtonHover: residential.greyLighter,
    tertiaryButtonProcessing: residential.greyLighter,
    tertiaryButtonHoverText: residential.black,

    breadCrumbsText: residential.greyDark,
    breadCrumbsBackgroundHover: residential.greyLighter,
    breadCrumbsTextHover: residential.black,

    cancelButtonText: residential.blue,
    cancelButtonBorder: residential.blue,
    cancelButtonHoverText: residential.blueDark,
    cancelButtonHoverBorder: residential.blueDark,
    cancelButtonBackground: residential.dimGrayLighter,
    cancelButtonLabelText: residential.black,
    textareaBackground: residential.dimGrayLighter,

    destructiveButton: residential.red,
    destructiveButtonText: residential.white,
    destructiveButtonHover: residential.redLight,
    destructiveButtonProcessing: residential.redLight,

    semiDestructiveButton: residential.red,
    semiDestructiveButtonBackground: residential.white,
    semiDestructiveButtonHover: residential.redLightest,

    inconspicuousButtonText: residential.greyLight,

    searchButton: isCommercialBrand() ? commercial.grayDark : residential.blue,
    searchButtonHover: isCommercialBrand() ? commercial.grayDarker : residential.blueDark,
    searchIcon: residential.dimGray,
    systemStatusButtonBorder: residential.greyLighter,
    systemStatusButtonText: residential.greyDarker,
    systemStatusButtonBorderHover: residential.grey,

    selectButton: residential.greyLightest,
    selectButtonHover: residential.greyLighter,

    premiumWidgetActiveButton: residential.warmGrey,
    premiumWidgetActiveButtonText: residential.redOrange,
    premiumWidgetInactiveButton: residential.redOrange,

    // icons

    actionableIcon: residential.greyDarker,
    actionableIconDisabled: residential.greyLighter,
    actionableIconHover: residential.greyDarkest,
    clearIcon: residential.grey,
    clearIconHover: residential.greyDark,
    customerPin: residential.yellowDark,
    customerPinUnpinned: residential.greyLight,
    deselectedLegendCheckmarkHover: residential.greyDarker,
    deselectedLegendCheckmark: residential.greyLight,
    dropdown: residential.greyDark,
    emptyNote: residential.greyDark,
    filledNote: residential.yellowDark,
    filterIcon: residential.blue,
    EditIcon: residential.blueDark,
    questionMarkIcon: residential.blue,
    selectedLegendCheckmark: residential.greyDarkest,
    notificationDot: residential.red,

    destructiveIcon: residential.redLighter,
    destructiveIconHover: residential.red,

    premiumLockedIcon: residential.red,
    premiumUnlockedIcon: residential.green,

    hehmNotesEditIcon: residential.blue,
    hehmNotesEditIconHover: residential.blueDark,
    hehmNotesDeleteIcon: residential.blue,
    hehmNotesDeleteIconHover: residential.redDark,
    hehmNotesSaveIcon: residential.blue,
    hehmNotesSaveIconHover: residential.blueDark,
    hehmNotesCancelIcon: residential.blue,
    hehmNotesCancelIconHover: residential.blueDark,

    // rth chart

    infoPanelHeaderBackground: residential.greyLighter,
    infoPanelContentBackground: residential.greyLightest,

    awairCo2: rthChart.teal,
    awairHumidity: rthChart.brown,
    awairPm25: rthChart.green,
    awairTemp: rthChart.magenta,
    awairVoc: rthChart.purple,

    indoorUnitActualAirflow: residential.yellow,
    legendOutdoor: residential.yellowDarker,
    legendOdLiquidPressure: residential.purple,
    legendOdLiquidTemp: residential.orange,
    legendOdSubCool: residential.greenDark,
    legendOdSuctionPressure: residential.bluegreen,
    legendOdSuctionTemperature: residential.purpleDark,
    legendOdSuperheat: residential.redDark,
    legendRelieving: residential.greyDark,
    legendIndoor: rthChart.blue,
    legendOdEevStepColor: rthChart.lavenderBlue,
    legendOdCoilTemp: residential.redPink,
    legendOdCurrent: residential.teal,
    legendReturnStaticPressure: residential.cornflowerBlue,
    legendOdGasTemp: residential.purpleDark,
    legendOdGasPressure: residential.bluegreen,

    legendIdEevStepColor: rthChart.purpleLightest,
    legendIdSupplyAirTempColor: rthChart.purpleLighter,
    legendIdReturnAirTempColor: rthChart.purpleLight,
    legendIdCoilTemp: rthChart.paleRed,
    legendGasTemp: rthChart.red,
    legendIdSuperHeat: rthChart.darkRed,
    pillCooling: residential.blueDark,
    pillHeating: residential.redDark,
    utilitySavings: rthChart.greenLight,

    // consumer rth chart
    cool: consumerRTHChart.cool,
    coolStage2: consumerRTHChart.coolStage2,
    heat: consumerRTHChart.heat,
    heatStage2: consumerRTHChart.heatStage2,
    heatStage3: consumerRTHChart.heatStage3,
    heatAux: consumerRTHChart.heatAux,
    heatAuxStage2: consumerRTHChart.heatAuxStage2,
    heatAuxStage3: consumerRTHChart.heatAuxStage3,
    hoverBox: consumerRTHChart.hoverBox,
    hoverBoxBackground: consumerRTHChart.hoverBoxBackground,
    chartBackground: consumerRTHChart.chartBackground,
    rthDefrost: residential.orangeBright,
    rthIndoorFanOnly: residential.greenMuted,
    rthIntegratedDehumidification: residential.greenBright,
    rthQuietMode: residential.yellowGreen,
    rthHumidifier: residential.orangeLight,
    rthDehumidifier: residential.yellowBright,
    rthVentilator: residential.periwinkle,
    rthSystemMode: residential.greyDarkHot,
    rthChartBorder: residential.grayBorder,
    rthZoneColor1: consumerRTHChart.coolStage2,
    rthZoneColor2: residential.green,
    rthZoneColor3: residential.pinkBright,
    rthZoneColor4: residential.orangeVivid,
    rthZoneColor5: residential.tealBright,
    rthZoneColor6: residential.red,
    rthZoneColor7: residential.blueSoft,
    rthZoneColor8: residential.purpleMuted,
    rthZoneColor9: residential.greenForest,
    rthAlertGroupBadgeBackground: residential.grayDeep,

    // device metric graph

    deviceMetricRedStatus: residential.red,
    deviceMetricYellowStatus: residential.yellowDark,
    deviceMetricGreenStatus: residential.green,

    // Thermostat Box
    thermostatBoxBackground: residential.blueDarkest,
    thermostatBoxHeatSetpoint: residential.red,
    thermostatBoxCoolSetpoint: residential.blue,
    thermostatBoxForeground: residential.white,
    iconDropletBackground: residential.blueDark,
    DashboardThermostatBackground: residential.greyLighter,
    DashboardThermostatText: residential.greyDarker,
    DashboardConfigText: residential.greyDarker,
    DashboardThermostatBorder: residential.blueTranslucent,
    DashboardThermostatSetpont: residential.white,

    // Damper
    damperFloWDirection: residential.greyDark,
    damperBackground: residential.greyLighter,

    // Sys Config
    editText: residential.black,
    deleteIcon: residential.greyDarkest,

    // alerts
    criticalAlert: residential.brightRed,
    majorAlert: residential.orangeBrown,
    minorAlert: residential.brightGreen,
    minorAlertTab: residential.white,
    minorSdaAlertTab: residential.white,
    dirtyFilter: residential.white,

    criticalAlertInactive: residential.dimRed,
    criticalSdaAlertInactive: residential.dimPurple,
    majorAlertInactive: residential.dimYellow,
    majorSdaAlertInactive: residential.dimPurple,
    minorAlertInactive: residential.dimGreen,
    minorSdaAlertInactive: residential.dimPurple,

    // device metric
    needsAttention: residential.red,
    notOptimal: residential.yellow,
    good: residential.green,

    // connection status
    connected: residential.green,
    disconnected: residential.red,

    // Health Status
    great: residential.green,
    fair: residential.yellow,
    critical: residential.red,

    // service contract
    serviceContractActive: residential.blue,
    serviceContractExpired: residential.greyDarkHot,
    serviceContractNone: residential.greyDim,

    // Device Tabs
    inactiveTabBackground: residential.greyLighter,
    activeTabBackground: residential.greyLighter,
    activeTabHighlight: isCommercialBrand() ? commercial.grayDark : residential.blue,
    inactiveTabText: residential.grey,
    // Customer Adaption
    customerAdaptionOptedIn: residential.green,
    customerAdaptionSomeSystemsOptedIn: residential.greenDark,
    customerAdaptionOptedOut: residential.blue,
    customerAdaptionNoSystems: residential.greyLighter,

    premiumAdoptionMonthly: residential.green,
    premiumAdoptionYearly: residential.blue,
    premiumAdoptionFreeTrial: residential.greyLighter,
    premiumAdoptionExpired: residential.greyDarker,
    premiumPlanSelectionChecked: residential.bluePremiumLight,
    premiumPlanSelectionUnchecked: residential.greyLighter,
    premiumPlanSelectionHeaderChecked: residential.bluePremium,
    premiumPlanSelectionHeaderUnchecked: residential.greyLight,

    // Remote Config List
    remoteConfigPanelHeaderBackground: residential.yellow,
    remoteConfigPanelHeaderText: residential.greyDarkest,
    remoteConfigExpireText: residential.greyDarker,

    pageTitle: residential.dimGray,
    cardTitle: residential.greyDarker,
    inactiveCardTitle: residential.dimGrey,
    cardDescription: residential.dimGray,
    statusText: residential.dimGray,
    inactiveCardText: residential.dimGrey,
    searchPlaceholder: residential.dimGrayLight,
    username: residential.dimGray,

    // Remote access Link Systems
    modalTitle: residential.dimGray,
    modalText: residential.dimGray,
    modalGridBackground: residential.dimGrayLighter,

    // Help videos icon
    helpVideoLink: residential.blue,
    iconBackground: residential.white,

    // Maintenanace Trends
    trendHeaderBackground: residential.greyLight,
    maintenancePanelBackground: residential.dimGrayLighter,
    maintenanceChartBackground: residential.dimGrayLighter,
    maintenancePlotBandBackground: residential.dimGrayLightest,
    maintenanceChartText: residential.greyDarkest,
    maintenanceTrendHoverBoxBackground: residential.white,
    maintenanceTrendHoverBoxBoarder: residential.greyDarkest,

    // AlertLookup
    alertDescriptions: residential.greyDark,
    alertDescriptionsHover: residential.greyDarkest,
    alertLookupButton: residential.blue,
    alertLookupButtonHover: residential.blueDarker,

    // Advance Customer Filter
    customerFilterBorder: residential.greyDark,
    customerFilterGroup: residential.dimGrayLighter,
    customerFilterText: residential.greyDarker,
    customerFilterHoverBorder: residential.blueDark,
    customerFilterHoverBackground: residential.blueLight,
    customerFilterSelected: residential.blueDark,
    customerFilterSelectedBorder: residential.blueDark,
    customerFilterSelectedBackground: residential.blueLighter,
    customerFilterButtonText: residential.white,

    // Admin Banner
    adminBannerProduction: residential.red,
    adminBannerDevelopment: residential.cornflowerBlue,
    adminBannerStaging: residential.yellowOrange,
    adminBannerQA: rthChart.teal,

    // dashboard
    draggableDropIndicator: residential.blue,
    cardAddedFlashBackground: residential.blue,
    draggableCardBackground: residential.blackTranslucent,

    // system dashboard
    LastUpdatedText: residential.dimGray,
    dashboardBackground: residential.greyLighter,
    dashboardCardBackground: residential.white,
    dashboardCardHoverBorder: residential.blue,
    disabledTextColor: residential.greyLight,
    dashboardSecondaryText: residential.greyDarker,

    emailBackground: residential.cloudGray,

    enrolledIcon: residential.teal,
    enrolledBackground: residential.tealLight,

    // system dashboard cards
    headerIcon: commercial.grayDarker,
    healthCritical: residential.brightRed,
    healthFair: residential.orangeBrown,
    healthCalibrating: residential.tealDark,
    healthCriticalInactive: residential.dimRed,
    healthFairInactive: residential.dimYellow,
    healthUnknown: residential.white,
    healthGreatInactive: residential.white,
    healthGreat: residential.brightGreen,
    criticalSdaAlert: residential.softPurple,
    majorSdaAlert: residential.softPurple,
    minorSdaAlert: residential.softPurple,
    recentlyDisconnected: residential.black,
    disconnected24Hours: residential.greyDarkerer,
    disconnected7Days: residential.dimGrey,
    dashboardCardBorder: residential.lightGray,
    buttonBackground: residential.alabasterGray,
    cardEmptyBackground: residential.grayLightest,
    warningSdaAlert: residential.orangeBrown,

    // date range picker
    dateRangeDayBackground: residential.blueLightest,
    dateRangeDayBackgroundHover: residential.blueLighter,
    dateRangeDayBoundary: residential.blue,
    dateRangeDayBoundaryHover: residential.blueDark,

    // hehm
    billingAddressBox: residential.greyBlue,
    billingButton: residential.greyCharcoal,
    upgradeButton: commercial.grayDarker,
    hehmAccent: paywallFeatureCards.hehm.accent,
    hehmAccentLight: residential.coolGrey,
    hehmCardGradientTopLeft: paywallFeatureCards.hehm.card.gradientTopLeft,
    hehmCardGradientFiftyPercent: paywallFeatureCards.hehm.card.gradientFiftyPercent,
    hehmCardGradientBottomRight: paywallFeatureCards.hehm.card.gradientBottomRight ?? '#E3E5F3',
    hehmButtonGradientTopLeft: paywallFeatureCards.hehm.button.gradientTopLeft,
    hehmButtonGradientFiftyPercent: paywallFeatureCards.hehm.button.gradientFiftyPercent,
    hehmButtonGradientBottomRight: paywallFeatureCards.hehm.button.gradientBottomRight ?? '#8E96D9',
    hehmBorderGradientTopLeft: paywallFeatureCards.hehm.border?.gradientTopLeft ?? '#989BB4',
    hehmBorderGradientFiftyPercent: paywallFeatureCards.hehm.border?.gradientFiftyPercent ?? '#D2D5EC',
    hehmQuantityBoxBackground: residential.white,
    hehmQuantityText: residential.brightGreen,
    inactiveAccountModalBorder: residential.black,
    termsCheckbox: residential.black,
    hehmCardBackground: residential.veryLightGray,
    hehmCardBorder: residential.darkGray,
    primaryAccent: residential.pastelBlue,
    hehmOracleTableBackground: residential.greyTranslucent,

    // premium
    premiumAccent: paywallFeatureCards.premium.accent,
    premiumCardGradientTopLeft: paywallFeatureCards.premium.card.gradientTopLeft,
    premiumCardGradientFiftyPercent: paywallFeatureCards.premium.card.gradientFiftyPercent,
    premiumCardGradientBottomRight: paywallFeatureCards.premium.card.gradientBottomRight ?? '#FFEBE7',
    premiumButtonGradientTopLeft: paywallFeatureCards.premium.button.gradientTopLeft,
    premiumButtonGradientFiftyPercent: paywallFeatureCards.premium.button.gradientFiftyPercent,
    premiumButtonGradientBottomRight: paywallFeatureCards.premium.button.gradientBottomRight ?? '#FF2B00',

    // chatbot
    chatbotHistoryBackground: residential.grayLight,
    chatbotBackground: residential.white,
    chatbotChatBoxBorder: residential.purpleLight,
    chatbotChatBoxGradientBorder: residential.brightGreenLight,
    chatbotText: residential.black,
    chatbotInfoText: residential.greyDarker,
    chatbotInputBackground: residential.dimBlue,
    chatbotInputPlaceholder: residential.greyDarker,
    chatbotInputText: residential.black,
    chatbotInputBorder: residential.dimBlue,
    chatbotQuestion: residential.blue,
    chatbotMessage: residential.white,
    chatbotReply: residential.greyDarkest,
    chatbotMessageBackground: residential.blue,
    chatbotReplyBackground: residential.greyBlue,
    chatbotFeedbackIconActive: residential.grey,
    chatbotConversationMonth: residential.greyDarker,
    chatbotSourceListBackground: residential.grayLightest,
    chatInfoIcon: residential.redOrange,

    // components
    dropDownShadow: residential.translucent,
    shadowSubtle: residential.translucentLightest,
    shadowSoft: residential.translucentSoft,
    shadowLight: residential.translucentLighter,
    shadowMedium: residential.translucentMedium,
    shadow: residential.translucentLight,
    shadowDark: residential.translucentDark,
    shadowDarker: residential.translucentDarker
  }
}
